import { usePage } from "@inertiajs/react";

import type { GroupKey } from "@/constants/groups";
import { isAllowed } from "@/lib/authz";
import type { SharedData } from "@/types";

interface GroupGateProps {
  /** Render children if user belongs to any of these groups. Optional. */
  anyOf?: GroupKey[];
  /** Render children only if user belongs to all of these groups. Optional. */
  allOf?: GroupKey[];
  /** Hide children if user belongs to any of these groups. Optional. */
  noneOf?: GroupKey[];
  /** Content to render when access is denied. Defaults to null (hidden). */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * GroupGate — universal UI gate by user groups (Inertia shared props)
 *
 * Examples:
 *  - Basic allow (any of):
 *      <GroupGate anyOf={["buyer", "supervisor"]}>...</GroupGate>
 *
 *  - Require multiple groups (all of):
 *      <GroupGate allOf={["buyer", "requester"]}>...</GroupGate>
 *
 *  - Combine rules (any + none):
 *      <GroupGate anyOf={["buyer"]} noneOf={["suspended"]}>...</GroupGate>
 *
 *  - With fallback content:
 *      <GroupGate anyOf={["supervisor"]} fallback={<span />}>...</GroupGate>
 */
export function GroupGate({ anyOf, allOf, noneOf, fallback = null, children }: GroupGateProps) {
  const { auth } = usePage<SharedData>().props;
  const groups = auth?.groups ?? [];

  if (!isAllowed(groups, { anyOf, allOf, noneOf })) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

export default GroupGate;
